/* eslint-disable @next/next/next-script-for-ga */
import { ChakraProvider } from '@chakra-ui/react';
import { User as FirebaseUser } from 'firebase/auth';
import type { AppInitialProps, AppProps } from 'next/app';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import getConfig, { getPublicRuntimeConfig } from '../common/config';
import { TWITTER_ACCOUNT_CORPORATE } from '../common/constants';
import {
  isAntaaAccount,
  isSlideAndAntaaAccount,
} from '../common/libs/accountType';
import Firebase from '../common/libs/firebase';
import * as analytics from '../common/libs/googleAnalytics';
import {
  setLogRocketUser,
  useLogRocket,
} from '../common/libs/logRocketForArticleUpload';
import { title } from '../common/libs/metadata';
import monitoring from '../common/libs/monitoring';
import { isUnauthenticatedStatus } from '../common/libs/user';
import type { NextPageWithLayout } from '../common/types';
import Layout from '../components/Layout';
import { AppContextProvider, useAppContext } from '../contexts/app';
import useIdApiClient from '../hooks/useIdApiClient';
import useApiClient from '../hooks/useApiClient';
import { useLogout, getIsManualLogout } from '../hooks/useLogout';
import { patchBigInt } from '../server/patch/bigInt';
import '../styles/globals.css';
import theme from '../theme';

// todo: next/scriptを使用してscriptタグの書き換え
// import Script from 'next/script';

// JSON.stringify() で `TypeError: Do not know how to serialize a BigInt`
// が出る不具合のパッチ
patchBigInt();

/**
 * 最後にID基盤ログインAPIを呼び出したFirebaseユーザーのUID
 * onIdTokenChanged はトークンリフレッシュの度に発火するため、
 * 同一ユーザーのトークンリフレッシュ時にログインAPIの重複呼び出しを防ぐ
 */
let lastLoggedInUid: string | null = null;

type AppPropsWithLayout = AppProps<any> & {
  Component: NextPageWithLayout;
};

function useAuth({ pageProps }: AppInitialProps) {
  const { setUser } = useAppContext();

  useEffect(() => {
    if (pageProps.auth) {
      setUser(pageProps.auth.user);
      if (pageProps.auth.user) {
        analytics.setUserId(pageProps.auth.user.id);
        monitoring.setUser(pageProps.auth.user.id);
        setLogRocketUser(
          pageProps.auth.user.id,
          pageProps.auth.user.discloseId || null,
        );
      }
    }
  }, [pageProps, setUser]);
  return null;
}

function useCsrf({ pageProps }: AppInitialProps) {
  const { setCsrfToken } = useAppContext();
  useEffect(() => {
    if (pageProps.csrfToken) {
      setCsrfToken(pageProps.csrfToken);
    }
  }, [pageProps, setCsrfToken]);
  return null;
}

// 利用規約の同意の確認をする
function useTermsOfServiceAgreement({ pageProps }: AppInitialProps) {
  const { termsOfServiceAgreement, setTermsOfServiceAgreement } =
    useAppContext();
  useEffect(() => {
    if (
      pageProps.auth &&
      pageProps.auth.user &&
      pageProps.auth.user.isTermsOfServiceAgree !== termsOfServiceAgreement
    ) {
      setTermsOfServiceAgreement(pageProps.auth.user.isTermsOfServiceAgree);
    }
  }, [pageProps, termsOfServiceAgreement, setTermsOfServiceAgreement]);
  return null;
}

// プライバシーポリシーの同意の確認をする
function usePrivacyPolicyAgreement({ pageProps }: AppInitialProps) {
  const { privacyPolicyAgreement, setPrivacyPolicyAgreement } = useAppContext();
  useEffect(() => {
    if (
      pageProps.auth &&
      pageProps.auth.user &&
      pageProps.auth.user.isPrivacyPolicyAgree !== privacyPolicyAgreement
    ) {
      setPrivacyPolicyAgreement(pageProps.auth.user.isPrivacyPolicyAgree);
    }
  }, [pageProps, privacyPolicyAgreement, setPrivacyPolicyAgreement]);
  return null;
}

function usePageView() {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      analytics.pageView(path);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);
}

function GaScript() {
  const {
    gaEnableTracking,
    gaTrackingId,
    gaMeasurementId,
    gaContainerId,
    gaConversionId,
  } = getPublicRuntimeConfig();

  if (!gaEnableTracking) {
    return null;
  }

  return (
    <>
      <script
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${gaTrackingId}', {
                    page_path: window.location.pathname,
                  });
                  gtag('config', '${gaMeasurementId}', {
                    page_path: window.location.pathname,
                    groups: 'default,ga4',
                  });
                  ${
                    gaConversionId
                      ? `gtag('config', '${gaConversionId}', {
                    page_path: window.location.pathname,
                  });`
                      : ''
                  }`,
        }}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer', '${gaContainerId}');
              `,
        }}
      />
    </>
  );
}

function IeEndOfSupportScript() {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
          if (navigator.userAgent.indexOf('MSIE') >= 0 ||
              navigator.userAgent.indexOf('Trident') >= 0) {
            window.location.href = '/ie-eos.html';
          }
        `,
      }}
    />
  );
}

function Head() {
  const { slideWebBaseUrl, isProduction } = getPublicRuntimeConfig();
  const noindex = !isProduction;

  const siteName = 'Antaa Slide';
  const _title = title('医師・医学生のためのスライド共有');
  const description =
    '医師/医学生向けスライド共有「Antaa Slide」は、医師が自ら作成・投稿した勉強会やセミナーのスライドを、互いに閲覧・学習できるスライド共有サービスです。実臨床にすぐに役立つ内容が豊富に揃い、日本最大級の医学スライドシェアとして、2300件超の実践的なスライドから無料で学ぶことが出来ます。';
  const imageUrl = `${slideWebBaseUrl}/images/ogp.png`;

  return (
    <NextHead>
      <title>{_title}</title>
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      <meta name="description" content={description} />
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

      <meta property="og:locale" content="ja_JP" key="og:locale" />
      <meta property="og:title" content={_title} key="og:title" />
      <meta property="og:site_name" content={siteName} key="og:site_name" />
      <meta property="og:type" content="website" key="og:type" />
      <meta
        property="og:description"
        content={description}
        key="og:description"
      />
      <meta property="og:image" content={imageUrl} key="og:image" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_ACCOUNT_CORPORATE} />

      <GaScript />
      <IeEndOfSupportScript />

      {noindex && <meta name="robots" content="noindex" />}
    </NextHead>
  );
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const { idApiClient, proceedIdPage } = useIdApiClient();
  const { apiClient } = useApiClient();
  const {
    user,
    setUser,
    firebaseUser,
    setFirebaseUser,
    setAccountIntegrationStatus,
  } = useAppContext();
  const { logout } = useLogout();
  const router = useRouter();
  const {
    publicRuntimeConfig: { qaWebBaseUrl, idBaseUrl },
  } = getConfig();

  // Use the layout defined at the page level, if available
  // @see https://nextjs.org/docs/basic-features/layouts#per-page-layouts
  const getLayout = Component.getLayout ?? ((page) => <Layout>{page}</Layout>);

  const onFirebaseUserChanged = useCallback(
    async (user: FirebaseUser | null) => {
      setFirebaseUser(user);
      if (user) {
        // 同一ユーザーのトークンリフレッシュ（※）時はID基盤ログインAPIをスキップ
        // ※トークンリフレッシュの契機は以下の2種類がある
        //   ・トークン有効性確認のための getIdToken(true) のポーリング
        //   ・IDトークン認証期限切れ時のID基盤）トークンリフレッシュAPIコール
        if (user.uid !== lastLoggedInUid) {
          lastLoggedInUid = user.uid;
          // ID基盤ログイン
          const res = await idApiClient('login', {
            method: 'POST',
          });
          if (res.status === 200) {
            const json = await res.json();
            setAccountIntegrationStatus(
              json['accountIntegrationStatus'] ?? null,
            );
          }
        }
      } else {
        lastLoggedInUid = null;
        setAccountIntegrationStatus(null);
      }
    },
    [idApiClient, setAccountIntegrationStatus],
  );

  useAuth({ pageProps });
  useCsrf({ pageProps });
  useTermsOfServiceAgreement({ pageProps });
  usePrivacyPolicyAgreement({ pageProps });
  Firebase.initFirebase(onFirebaseUserChanged);

  // GAにPV送信
  usePageView();
  useLogRocket(getPublicRuntimeConfig().logRocketId);

  /**
   * Antaaアカウントで、利用規約またはプライバシーポリシー未同意
   * の場合は、ID基盤側の同意画面を表示する
   */
  useEffect(() => {
    const accountType = pageProps.auth?.user?.accountType;
    if (!accountType) return;

    if (isAntaaAccount(accountType) || isSlideAndAntaaAccount(accountType)) {
      if (!pageProps.auth.user.isTermsOfServiceAgree) {
        proceedIdPage(
          '/users/terms-of-service/agreement?from_service=slide',
          pageProps.auth?.user?.discloseId,
        );
      } else if (!pageProps.auth.user.isPrivacyPolicyAgree) {
        proceedIdPage(
          '/users/privacy-policy/agreement?from_service=slide',
          pageProps.auth?.user?.discloseId,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageProps.auth?.user?.accountType,
    pageProps.auth?.user?.isPrivacyPolicyAgree,
    pageProps.auth?.user?.isTermsOfServiceAgree,
  ]);

  /**
   * ログイン中にアカウントが無効化した場合はログアウトさせる
   */
  useEffect(() => {
    const user = pageProps.auth?.user;
    if (!user || !user.userStatus) return;
    const userStatus = user.userStatus.toUpperCase();
    if (isUnauthenticatedStatus(userStatus)) {
      logout();
    }
  }, [logout, pageProps.auth?.user]);

  /**
   * Firebase認証の無効化検知による強制ログアウト
   *
   * 他クライアントからID基盤 POST /api/logout が呼ばれ、
   * revoke_refresh_tokens でFirebaseリフレッシュトークンが無効化された場合、
   * Firebase SDKがトークン更新失敗を検知し onIdTokenChanged(null) が発火する。
   * この時、Slide Web側のPassportセッション（sess/sess.sig）と
   * remember-me Cookieもクリアし、確実にログアウト状態にする。
   */
  const prevFirebaseUserRef = useRef<FirebaseUser | null | undefined>(
    undefined,
  );

  useEffect(() => {
    const wasAuthenticated =
      prevFirebaseUserRef.current !== undefined &&
      prevFirebaseUserRef.current !== null;
    prevFirebaseUserRef.current = firebaseUser;

    if (
      wasAuthenticated &&
      firebaseUser === null &&
      user &&
      !getIsManualLogout()
    ) {
      const forceLogout = async () => {
        // Slide Webのセッション（sess/sess.sig）とremember-me Cookieをクリア
        try {
          await apiClient('/api/logout', { method: 'POST' });
        } catch (e) {
          // API呼び出し失敗時も続行する
        }
        // ID基盤のCookie（__Secure-id, __Secure-id-rt）をクリア
        // credentials: 'include' により既存Cookieが送信され、
        // レスポンスのSet-CookieでブラウザからCookieが削除される
        try {
          await idApiClient('logout', { method: 'POST' });
        } catch (e) {
          // ID基盤ログアウト失敗時も続行する
        }
        setUser(null);
        monitoring.setUser();
        setLogRocketUser();
        router.push('/login');
      };
      forceLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser]);

  return (
    <>
      <Head />
      <ChakraProvider theme={theme}>
        {getLayout(<Component {...pageProps} />)}
      </ChakraProvider>
    </>
  );
}

function AppWithProviders(props: AppPropsWithLayout) {
  return (
    <AppContextProvider>
      <App {...props} />
    </AppContextProvider>
  );
}

export default AppWithProviders;
